import React, { useEffect } from "react";
import {
  ActionIcon,
  Avatar,
  Box,
  Button,
  Group,
  HoverCard,
  Menu,
  Modal,
  Switch,
  Text,
  Textarea,
  createStyles,
} from "@mantine/core";
import { IconDownload, IconEdit, IconTrash } from "@tabler/icons-react";
import { FiExternalLink } from "react-icons/fi";
import { useDisclosure } from "@mantine/hooks";
import { useState } from "react";
import swal from "sweetalert";
import { editPatient, removePatient, updateClientStatus } from "../../services/patient";
import PatientModal from "../PatientModal";
import { useDispatch, useSelector } from "react-redux";
import { decrypt } from "../../utils";
import { useNavigate } from "react-router-dom";
import { BiDotsVerticalRounded } from "react-icons/bi";
import { setSelectedClient } from "../../store/reducers/mar";
import moment from "moment";
import { pdf } from "@react-pdf/renderer";
import ClientSummaryPDF from "../ClientSummaryPDF/ClientSummaryPDF";
import { getActivites } from "../../services/activity";
import { saveAs } from "file-saver";
import { DateInput } from "@mantine/dates";
import DischargeReasonAndDate from "../DischargeReasonAndDate";
import { updateStaffStatus } from "../../services/staff";

const useStyles = createStyles((theme) => ({
  border: {
    borderLeft: "1px solid #F2F2F2",
  },
  dFlex: {
    display: "flex",
    alignItems: "center",
    gap: 30,
    height: "100%",
  },
  marginTop: {
    marginTop: "15px",
  },
  medicationModal: {
    width: "100%",
    overflow: "auto",
    borderRadius: "1rem 1rem 1rem 1rem",
    boxShadow: "0 0 10px rgba(0, 0, 0, 0.2)",
    "& .mantine-Paper-root": {
      overflow: "initial !important",
    },
    "& .mantine-Modal-inner": {
      padding: "0px !important",
    },
    "& .mantine-Modal-header": {
      display: "none",
    },
  },
  header: {
    width: "100%",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    background: "#f4ead8",
    borderRadius: "1rem 1rem 0 0",
    height: "80px",
    gap: "20px",
    "& h2": {
      margin: "0",
      fontSize: "20px",
    },
  },
  mainContentWarpper: {
    width: "100%",
    maxHeight: "auto",
    overflowY: "auto",
  },
  mainContent: {
    width: "100%",
    padding: "10px",
  },
  selectBoxDate: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "1rem",
    width: "100%",
    flexWrap: "wrap",
  },
  datePicker: {
    width: "250px",
    "& .mantine-DatePickerInput-input": {
      border: "none",
      borderRadius: "0px !important",
      borderBottom: "2px solid #A8A8A8 !important",
    },
    "@media (max-width: 600px)": {
      width: "100%",
    },
  },
  buttons: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: ".5rem",
    marginTop: "20px",
    marginBottom: "20px",
  },
  truncate: {
    overflow: "hidden",
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
    maxWidth: "150px",
  },
  dischargeBgColor: {
    backgroundColor: "#fcfcfc",
  },
}));

const PatientTableRow = ({ row, onEdit, selectedTab }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { classes } = useStyles();
  const [active, setActive] = useState(row.active);
  const [opened, { open, close }] = useDisclosure(false);
  const [view, setView] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [disableModalOpen, setDisableModalOpen] = useState(false);
  const [inActiveReason, setInActiveReason] = useState("");
  const [reasonError, setReasonError] = useState("");
  const [value, setValue] = useState(new Date());

  useEffect(() => {
    setActive(row.active);
  }, [row]);

  const { profile } = useSelector((state) => state.profile);

  const customId = row?.id;
  const clientId = customId?.slice(-6);

  // functions
  const remove = async () => {
    const willDelete = await swal({
      title: `Are you sure?`,
      text: "This action cannot be undone.",
      icon: "warning",
      dangerMode: true,
      buttons: ["Cancel", "Delete"],
    });
    if (willDelete) {
      try {
        await removePatient(row?.id, () => {}, selectedTab);
        swal("Success", "Client remove successfully", "success");
      } catch (reasonError) {
        swal("Error", "Failed to remove the Client", "error");
      }
    }
  };

  const handleClientSelect = (obj) => {
    dispatch(setSelectedClient(obj));
    localStorage.setItem("selectedClient", JSON.stringify(obj));
  };

  const updateStatus = async (event) => {
    if (!event.target.checked) {
      setDisableModalOpen(true);
    } else {
      setActive(false);
      setValue("");
      setInActiveReason("");
      await updateClientStatus({ id: row?.id, active: true }, true, selectedTab);
    }
  };

  function handleRowClick(event) {
    handleClientSelect(row);
    console.log("🚀 ~ handleRowClick ~ row:", row)
    navigate("/home/client", { state: row?.id });
  }

  const _getActivites = async () => {
    try {
      const data = await getActivites(customId);
      return data;
    } catch (error) {
      console.error("Failed to fetch activities:", error);
    }
  };

  const handlePdfDownload = async (fileName) => {
    setPdfLoading(true);
    const activitiesRes = await _getActivites();
    const blob = await pdf(
      <ClientSummaryPDF clientData={row} activities={activitiesRes} facilityName={profile?.business?.companyName || profile?.facility?.name} facilityPhoto={profile?.facility?.imageurl ? decrypt(profile.facility.imageurl) : null} />
    ).toBlob();
    saveAs(blob, fileName);
    setPdfLoading(false);
  };

  useEffect(() => {
    // Convert inactiveClientDate to Date object if it exists
    const initialDate = row?.inactiveClientDate 
      ? new Date(row.inactiveClientDate) 
      : new Date();
  
    setValue(initialDate);
    // setReason(row?.inActiveReason);
    setInActiveReason(row?.inActiveReason);
  }, [row]);

  const handleDischargeSave = async () => {
    if (inActiveReason.trim() === "") {
      setReasonError("Discharge reason is required.");
      return;
    }
    setActive(false);

    await updateClientStatus(
      {
        id: row?.id,
        active: false,
        inActiveReason,
        inactiveClientDate: value
      },
      true,
      selectedTab
    );
    setDisableModalOpen(false);
  };

  const handleDischargeClose = () => {
    setInActiveReason("");
    setReasonError("");
    setDisableModalOpen(false);
  };

  return (
    <>
      <tr style={{ cursor: "pointer" }}>
        <td onClick={handleRowClick}>
          <Box className={classes.dFlex}>
            <Avatar radius="xl" size="lg" src={decrypt(row?.imageurl)} />
            <Box>
              <Text fz={20}>{row?.name || "-"} </Text>
              <Text style={{ textTransform: "uppercase" }} color="dimmed">
                {clientId || "-"}
              </Text>
            </Box>
          </Box>
        </td>
        <td onClick={handleRowClick}>
          {row?.facilityId?.map((fac) => fac?.name)?.join(", ")}
        </td>
        <td id="dob" onClick={handleRowClick}>
          {moment(row.form1?.admissionDate).format("MM/DD/YYYY") || "-"}
        </td>
        <td onClick={handleRowClick}>
          {moment(row.form1?.dob).format("MM/DD/YYYY") || "-"}
        </td>
        <td onClick={handleRowClick}>
          {row.form2?.emergencyContactName || "-"}
        </td>
        <td onClick={handleRowClick}>
          {row.form2?.emergencyContactPhoneNumber || "-"}
        </td>
        {selectedTab !== true && (
          <>
            <td>
              <HoverCard width={300} shadow="md" position="bottom" withArrow>
                <HoverCard.Target>
                  <Text onClick={handleRowClick} className={classes.truncate}>
                    {row?.inActiveReason?.slice(0, 100) || "-"}
                    {row?.inActiveReason?.length > 100 && "..."}
                  </Text>
                </HoverCard.Target>
                <HoverCard.Dropdown>
                  <Text>{row?.inActiveReason || "No reason provided"}</Text>
                </HoverCard.Dropdown>
              </HoverCard>
            </td>
            <td onClick={handleRowClick}>
              {moment(row?.inactiveClientDate).format("MM/DD/YYYY") || "-"}
            </td>
            {/* {row?.active === false && (
              <td>
                <ActionIcon
                  id="edit"
                  color="yellow"
                  variant="light"
                  title="Edit"
                  onClick={updateStatus}
                >
                  <IconEdit />
                </ActionIcon>
              </td>
            )} */}
          </>
        )}
        <td className={classes.dFlex}>
          <Switch
            className={`${classes.StaffSwitchBtn} ${classes.marginTop}`}
            size="md"
            color="yellow"
            checked={active}
            onChange={updateStatus}
            disabled={
              profile?.user?.type === "staff" && profile?.user?.role !== "admin"
            }
          />
          <Group>
            <PatientModal
              opened={opened}
              close={close}
              title={"Edit Individual"}
              data={row}
              view={view}
              setView={setView}
            />
            <Menu shadow="md" position="left" zIndex={"1000"}>
              <Menu.Target>
                <ActionIcon className={classes.marginTop}>
                  <BiDotsVerticalRounded
                    id="vertical-dots"
                    size={20}
                    color="#000"
                  />
                </ActionIcon>
              </Menu.Target>
              <Menu.Dropdown>
                <Menu.Item
                  id="open-link"
                  icon={<FiExternalLink size={14} />}
                  onClick={() => {
                    navigate("/home/client", { state: row?.id });
                    handleClientSelect(row);
                  }}
                >
                  Open
                </Menu.Item>
                {/* <Menu.Item icon={<IconDownload size={14} />}>Download</Menu.Item> */}

                {/* <PDFDownloadLink
                style={{ textDecoration: "none" }}
                document={
                  <ClientSummaryPDF clientData={row} activities={activities} />
                }
                fileName={`${row?.form1?.name ?? "Client Summary"}.pdf`}
              >

                {({ blob, url, loading, error }) =>
                  loading ? (
                    <Menu.Item
                      disabled={true}
                      icon={<IconDownload size={14} />}
                    >
                      Download
                    </Menu.Item>
                  ) : (
                    <Menu.Item
                      disabled={false}
                      icon={<IconDownload size={14} />}
                    >
                      Download
                    </Menu.Item>
                  )
                }
              </PDFDownloadLink> */}

                <Menu.Item
                  disabled={pdfLoading}
                  icon={<IconDownload size={14} />}
                  onClick={() =>
                    handlePdfDownload(
                      `${row?.form1?.name ?? "Client Summary"}.pdf`
                    )
                  }
                >
                  Download
                </Menu.Item>

                {/* <Menu.Item
                  icon={<IconEdit size={14} />}
                  onClick={() => onEdit(row)}
                >
                  Edit
                </Menu.Item> */}
                {!(profile.user.type === 'staff' && profile.user.role === 'staff') && (
  <Menu.Item
    icon={<IconEdit size={14} />}
    onClick={() => onEdit(row)}
  >
    Edit
  </Menu.Item>
)}

                {row?.active === false && (
                  <Menu.Item
                    icon={<IconEdit size={14} />}
                    onClick={updateStatus}
                  >
                    Discharge Info
                  </Menu.Item>
                )}

                              {!(profile.user.type === 'staff' && profile.user.role === 'staff') && (
                <Menu.Item
                  icon={<IconTrash size={14} />}
                  color="red"
                  onClick={remove}
                >
                  Delete
                </Menu.Item>
                )}
              </Menu.Dropdown>
            </Menu>
          </Group>
        </td>
      </tr>
      {/* <----- Call modal conditionally to prevent unnecessary calls -----> */}

      {disableModalOpen && (
        <DischargeReasonAndDate
          value={value}
          setValue={setValue}
          row={row}
          selectedTab={selectedTab}
          disableModalOpen={disableModalOpen}
          handleDischargeClose={handleDischargeClose}
          reasonError={reasonError}
          inActiveReason={inActiveReason}
          handleDischargeSave={handleDischargeSave}
          setInActiveReason={setInActiveReason}
          setReasonError={setReasonError}
        />
      )}
    </>
  );
};

export default PatientTableRow;
